<template>
  <div 
    class="tutorial-menu-overlay"
    @click="handleOverlayClick"
  >
    <div 
      class="tutorial-menu"
      :style="menuStyles"
      @click.stop
    >
      <!-- Header -->
      <div class="menu-header">
        <h3 class="menu-title">Available Tutorials</h3>
        <button 
          class="close-button"
          @click="$emit('close')"
          aria-label="Close menu"
        >
          ×
        </button>
      </div>

      <!-- Tutorial list -->
      <div class="menu-content">
        <div 
          v-if="tutorials.length === 0"
          class="no-tutorials"
        >
          <div class="no-tutorials-icon">📚</div>
          <p>No tutorials available for this page</p>
        </div>

        <div v-for="(group, segmentName) in segmentedTutorials" :key="segmentName" class="tutorial-segment">
          <h4 class="segment-title" v-if="Object.keys(segmentedTutorials).length > 1 || segmentName !== 'Geral'">
            {{ segmentName }}
          </h4>
          
          <div 
            v-for="tutorial in group"
            :key="tutorial.id"
            class="tutorial-item"
            :class="{ 'tutorial-active': tutorial.isActive }"
            @click="startTutorial(tutorial.id)"
          >
            <div class="tutorial-info">
              <h5 class="tutorial-name">{{ tutorial.name }}</h5>
              <p 
                v-if="tutorial.description"
                class="tutorial-description"
              >
                {{ tutorial.description }}
              </p>
              <div class="tutorial-meta">
                <span class="tutorial-steps">
                  {{ tutorial.steps?.length || 0 }} steps
                </span>
                <span 
                  v-if="tutorial.isActive"
                  class="tutorial-status active"
                >
                  In Progress
                </span>
              </div>
            </div>
            
            <div class="tutorial-actions">
              <button 
                class="btn btn-primary btn-small"
                @click.stop="startTutorial(tutorial.id)"
              >
                {{ tutorial.isActive ? 'Continue' : 'Start' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="menu-footer">
        <button 
          class="btn btn-secondary btn-small"
          @click="$emit('close')"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { Tutorial, PecTutorConfig } from '../types';

// Props
interface Props {
  tutorials: Tutorial[];
  config: PecTutorConfig;
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  'start-tutorial': [tutorialId: string];
  close: [];
}>();

// Computed properties
const menuStyles = computed(() => ({
  borderColor: props.config.theme.primaryColor,
  backgroundColor: props.config.theme.backgroundColor,
  color: props.config.theme.textColor,
}));

const segmentedTutorials = computed(() => {
  const groups: Record<string, Tutorial[]> = {};
  
  // Default unsegmented group
  groups['Geral'] = [];

  props.tutorials.forEach(tutorial => {
    const seg = tutorial.segment || 'Geral';
    if (!groups[seg]) {
      groups[seg] = [];
    }
    groups[seg].push(tutorial);
  });

  // Remove empty groups
  if (groups['Geral'].length === 0) {
    delete groups['Geral'];
  }

  return groups;
});

// Methods
const handleOverlayClick = () => {
  emit('close');
};

const startTutorial = (tutorialId: string) => {
  emit('start-tutorial', tutorialId);
};
</script>

<style scoped>
.tutorial-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 10002;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: overlayAppear 0.2s ease;
}

.tutorial-menu {
  background: white;
  border-radius: var(--pec-tutor-border-radius, 8px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.3);
  width: 90vw;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  animation: menuAppear 0.3s ease;
  border: 2px solid var(--pec-tutor-primary, #007bff);
}

/* Header */
.menu-header {
  padding: 20px;
  background: linear-gradient(135deg, var(--pec-tutor-primary, #007bff), #0056b3);
  color: white;
  position: relative;
}

.menu-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s ease;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Content */
.menu-content {
  max-height: 400px;
  overflow-y: auto;
  padding: 20px;
}

.no-tutorials {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.no-tutorials-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.no-tutorials p {
  margin: 0;
  font-size: 16px;
}

.tutorial-segment {
  margin-bottom: 20px;
}

.segment-title {
  font-size: 14px;
  color: var(--pec-tutor-primary, #0056b3);
  margin: 0 0 12px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #e1e5e9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
}

/* Tutorial items */
.tutorial-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tutorial-item:hover {
  border-color: var(--pec-tutor-primary, #007bff);
  background: #f8f9fa;
}

.tutorial-item:last-child {
  margin-bottom: 0;
}

.tutorial-active {
  border-color: #28a745;
  background: linear-gradient(135deg, #f8fff8 0%, white 100%);
}

.tutorial-info {
  flex: 1;
  margin-right: 15px;
}

.tutorial-name {
  margin: 0 0 5px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--pec-tutor-text, #333);
}

.tutorial-description {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
}

.tutorial-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}

.tutorial-steps {
  color: #999;
}

.tutorial-status.active {
  background: #28a745;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
}

.tutorial-actions {
  flex-shrink: 0;
}

/* Footer */
.menu-footer {
  padding: 15px 20px;
  background: #f8f9fa;
  border-top: 1px solid #e1e5e9;
  text-align: right;
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-small {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-primary {
  background: var(--pec-tutor-primary, #007bff);
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-secondary {
  background: var(--pec-tutor-secondary, #6c757d);
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

/* Scrollbar */
.menu-content::-webkit-scrollbar {
  width: 6px;
}

.menu-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.menu-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.menu-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Animations */
@keyframes overlayAppear {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes menuAppear {
  0% { 
    opacity: 0; 
    transform: scale(0.9) translateY(-20px);
  }
  100% { 
    opacity: 1; 
    transform: scale(1) translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .tutorial-menu {
    width: 95vw;
    max-height: 85vh;
  }
  
  .tutorial-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .tutorial-info {
    margin-right: 0;
  }
  
  .tutorial-actions {
    align-self: flex-end;
  }
}
</style>
